import * as Sentry from "@sentry/browser";
import { Col, Row, Table, Tag, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import queryString from "query-string";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import ExportData from "../components/ExportData";
import ImportData from "../components/ImportData";
import {
  GET_FILE_OPTIONS_FOR_CUSTOMER,
  SEARCH_CUSTOMER_LIST,
} from "../gql/customers";
import {
  Customer,
  FileOption,
  RowSelection,
  searchQueryParams,
} from "../helpers/shared-interfaces";
import {
  loadSavedFilters,
  request,
  responseHandler,
  showTotalPagination,
} from "../helpers/utilities";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { extractPageFromQuery } from "../components/TableFilters";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  showAdvancedSearchModal: boolean;
  customers: Customer[];
  customersCount: number;
  page: number;
  pageSize: number;
  q: string;
  fileOptions: FileOption[];
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
  selectedIdsToExport: React.Key[];
}

function getCustomerColumns<T extends Customer = Customer>(
  requests: Customer[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "PetraID",
      dataIndex: "petraId",
      key: "petraId",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "company",
      key: "company",
      render(text: string, record: Customer): JSX.Element {
        return <span>{record.fullname}</span>;
      },
    },
    {
      title: "SSNIT",
      dataIndex: "ssnitNumber",
      key: "ssnitNumber",
      width: "10%",
      render(text: string): JSX.Element {
        if (text) return <span>{text}</span>;
        return (
          <Tag color="red" key="ssnit">
            Not Assigned
          </Tag>
        );
      },
    },

    {
      title: "Email",
      dataIndex: "customerEmails",
      key: "customerEmails",
      width: "20%",
      render(text: string, record: Customer): JSX.Element {
        const email = record?.customerEmails?.[0]?.email ?? "";
        return (
          <span>
            {email ? (
              <div key="cEmail">
                <span>{email.address}</span>
              </div>
            ) : (
              <Tag color="red" key="cEmailNotAssigned">
                No Email
              </Tag>
            )}
          </span>
        );
      },
    },

    {
      title: "Phone",
      dataIndex: "activeCustomerPhones",
      key: "activeCustomerPhones",
      width: "15%",
      render(text: string, record: Customer): JSX.Element {
        const phone = record?.activeCustomerPhones?.[0]?.phone ?? "";
        return (
          <span>
            {phone ? (
              <div key="cPhone">
                <span>{phone.number}</span>
              </div>
            ) : (
              <Tag color="red" key="cPhoneNotAssigned">
                No Mobile Number
              </Tag>
            )}
          </span>
        );
      },
    },
  ];
}

export default class Customers extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      customers: [],
      customersCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      showAdvancedSearchModal: false,
      fileOptions: [],
      selectedFieldsToDisplay: [],
      selectedIdsToExport: [],
    };
    this.reference = React.createRef<FormInstance>();
    this.nextPage = this.nextPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    if (loadSavedFilters("customer").length < 1) {
      await this.triggerImmediateSearch();
    }
    await this.getFileOptions();
  }

  getFileOptions = async (): Promise<void> => {
    request({
      query: GET_FILE_OPTIONS_FOR_CUSTOMER,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          fileOptionsForCustomer: FileOption[];
        };
      }>): void => {
        this.setState({
          fileOptions: data.data.fileOptionsForCustomer,
        });
      },
    );
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      { isLoading: true, page, pageSize: pageSize },
      async () => {
        if (this.state.selectedFieldsToDisplay.length < 1) {
          await this.triggerImmediateSearch();
        }
        this.setState({ isLoading: false });
      },
    );
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value, page: 1 });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page, pageSize } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isLoading: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER_LIST,
        variables: { q, page, numberPerPage: pageSize },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "customerSearch",
      );

      if (handled) {
        message.error("Failed To Load Customers");
      } else {
        await this.syncBrowserQueryString();
        this.setState({
          customers: mutation.results,
          customersCount: mutation.count,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

    extractQueryParamsFromUrl = (): searchQueryParams => {
      const parsed = queryString.parse(window.location.search);
      return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
    };

    mergeWithLocalState = async (): Promise<void> => {
      const parsed = this.extractQueryParamsFromUrl();
      let q = "";
      let page = 1;

      if (parsed.q) {
        q = parsed.q;
      }

      if (parsed.page) {
        page = extractPageFromQuery(parsed.page);
      }

      this.setState({
        q,
        page,
      });
    };

    syncBrowserQueryString = (): void => {
      const { q, page } = this.state;
      const queryStrings = [];

      if (q) {
        queryStrings.push(`q=${q}`);
      }

      queryStrings.push(`page=${page}`);

      const queryString = `?${queryStrings.join("&")}`;

      this.props.history.replace(queryString);
    };


    toggleAdvancedSearchModel = (): void => {
      const prevState = this.state.showAdvancedSearchModal;
      this.setState({ showAdvancedSearchModal: !prevState });
    };

    handleOnAdvancedSearchData = (
      data: AdvancedSearchReturn,
      fields: IField[],
    ) => {
      this.setState({
        customers: data.results as Customer[],
        selectedFieldsToDisplay: fields,
        customersCount: data.count,
      });
    };

    handleOnAdvancedSearchError = (error: string) => {
      message.error("Failed to execute query");
      Sentry.captureException(error);
    };

    handleOnFieldRemove = (field: IField) => {
      const fields = this.state.selectedFieldsToDisplay.filter(
        (f) => f.name !== field.name,
      );
      this.setState(
        {
          removedField: field,
          selectedFieldsToDisplay: fields,
        },
        () => {
          localStorage.setItem(
            "customer",
            JSON.stringify(this.state.selectedFieldsToDisplay),
          );
        },
      );
      if (fields.length === 0) this.triggerDelayedSearch();
    };



    onSelectChange(newSelectedIdsToExport: React.Key[]) {
      this.setState({
        selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
          (item) => item,
        ),
      });
    }

    render(): JSX.Element | null {
      const {
        isLoading,
        customers,
        customersCount,
        page,
        pageSize,
        q,
        selectedFieldsToDisplay,
        removedField,
        selectedIdsToExport,
      } = this.state;

      const resultsWithKeys: Customer[] = customers.map(
        (result: Customer): Customer => {
          return {
            ...result,
            key: result.id,
          };
        },
      );

      // rowSelection object indicates the need for row selection
      const rowSelection: RowSelection = {
        selectedIdsToExport,
        type: "checkbox",
        onChange: this.onSelectChange,
      };


      return (
        <>
          <AdvancedSearchModal
            toggleModal={this.toggleAdvancedSearchModel}
            showModal={this.state.showAdvancedSearchModal}
            model="customer"
            onError={this.handleOnAdvancedSearchError}
            onData={this.handleOnAdvancedSearchData}
            triggerField={removedField?.name}
            pageSize={pageSize}
            page={page}
          />
          <Card>
            <div className="customers-container">
              <PageToolbar
                searchPlaceholder="Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ..."
                searchValue={q}
                onSearchChange={this.handleSearchInput}
                totalCount={customersCount}
                currentDataCount={customers.length}
                page={page}
                pageSize={pageSize}
                selectedCount={selectedIdsToExport.length}
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                primaryAction={
                  <ImportData
                    model="Customer"
                    title="Import Customer Data"
                    buttonText="Import Customer Data"
                    fileOptions={this.state.fileOptions}
                  />
                }
                actions={
                  <ExportData
                    model={"customer"}
                    dataArray={customers}
                    size={"large"}
                    customIds={selectedIdsToExport}
                  />
                }
                modelName="customers"
              />

              <Row>
                <Col span={24}>
                  <Table
                    loading={isLoading}
                    scroll={{ x: 400 }}
                    columns={getCustomerColumns(customers)}
                    dataSource={resultsWithKeys}
                    onRow={(record) => ({
                      onClick: () => {
                        this.props.history.push({
                          pathname: `/customers/${record.id}`,
                          state: { type: "customer", requests: customers },
                        });
                      },
                      style: { cursor: "pointer" },
                    })}
                    pagination={{
                      current: page,
                      defaultPageSize: pageSize,
                      total: customersCount,
                      onChange: this.nextPage,
                      showTotal: showTotalPagination,
                    }}
                    rowSelection={rowSelection}
                  />
                </Col>
              </Row>
            </div>
          </Card>
        </>
      );
    }
  };
